import date from "date-and-time";
import { Combobox } from "@headlessui/react";
import { ChevronRightIcon } from "@heroicons/react/24/outline";
import { useState } from "react";
import { IContact } from "../../models/Contact";
import { ILead } from "../../models/Lead";
import classNames from "../../utils/classNames";
import useEmails from "../../data/useEmails";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import useCustomerDetails from "../../data/useCustomerDetails";
import formatDateTime from './../../utils/formatDateTime';
import { DateTime } from "luxon";


export default function SendEmail({ lead, setOpen }: { lead: ILead, setOpen: any }) {
  const { sendEmail } = useEmails();
  const { saveHandlers } = useDefaultCRUDHandlers("Email sent");
  const [selected, setSelected] = useState<any>({ id: undefined });
  const [email, setEmail] = useState<string>(lead.contact?.email!);
  const [content, setContent] = useState<string>("");

  const { customer } = useCustomerDetails();

  const emailTemplates = [
    {
      id: 1,
      title: "Enquiry Received",
      content: `Hi ${lead.contact?.fullname},\n\nI hope you are well. I am contacting you regarding your recent enquiry. I am writing to inform you that we have received your request and it is currently being processed. We will be in touch with you shortly to let you know when we're ready to collect your vehicle.\n\nKind regards,\n\n${customer.data?.data.name || ""}`,
    },
    {
      id: 2,
      title: "Vehicle Collection",
      content: `Hi ${lead.contact?.fullname.trim()}, \n\nThis email is confirming that you pickup date and time is ${formatDateTime(lead.vehicle?.scheduledCollectionOn, DateTime.DATETIME_MED, "")}. \n\nKind regards,\n\n${customer.data?.data.name || ""}`,
    },
    {
      id: 3,
      title: "No Contact",
      content: `Hi ${lead.contact?.fullname.trim()}, \n\nThe collection company have been trying to contact you to arrange a convenient collection time/date for your ${lead.vehicle?.combinedMake || "vehicle"} ${lead.vehicle?.modelRangeDescription?.toUpperCase() || ""}. \n\nCan you please contact ${customer.data?.data.name || ""} on ${customer.data?.data.telephone || ""} and quote your registration number to arrange a collection.\n\nIf you would like to cancel, please reply to this email saying 'Please Cancel My Collection' \n\nThank you in advance, \n\nKind regards,\n\n${customer.data?.data.name || ""} \n\n${customer.data?.data.email || ""}\n${customer.data?.data.telephone || ""}`,
    },
    {
      id: 4,
      title: "Confirm Collection",
      content: `Hi ${lead.contact?.fullname.trim()}, \n\nWe have your ${lead.vehicle?.combinedMake || "vehicle"} ${lead.vehicle?.modelRangeDescription?.toUpperCase() || ""} booked in for TODAY. \n\nPlease call ${customer.data?.data.telephone || ""} before 5pm if you would like to cancel or re-book. \n\nPlease note if they do not hear back from you in the first instance, your collection may not go ahead.\n\nKind regards,\n\n${customer.data?.data.name || ""} \n\n${customer.data?.data.email || ""}\n${customer.data?.data.telephone || ""}`,
    },
    {
      id: 5,
      title: "Wrong Number",
      content: `Hi ${lead.contact?.fullname.trim()}, \n\nIt seems we have an incorrect telephone number for you. \n\nCan you please contact us on ${customer.data?.data.telephone || ""} and quote your registration number ${lead.vehicle?.vrm || ""} to confirm a correct contact telephone number.\n\nIf you would like to cancel, please reply to this email saying 'Please Cancel My Collection'\n\nKind regards,\n\n${customer.data?.data.name || ""} \n\n${customer.data?.data.email || ""}\n${customer.data?.data.telephone || ""}`,
    }
  ]

  const handleSetSelected = (template: any) => {
    setSelected(template);
    setContent(template.content);
  }

  const handleSendEmail = () => {
    console.log("email", email);
    console.log("content", content);

    var emailToSend = {
      ContactId: lead.contact?.id,
      Recipient: email,
      Body: content,
      Name: "",
      Subject: selected.title
    }

    sendEmail.mutate(emailToSend, {
      onSuccess: () => {
        setOpen(false);
        saveHandlers.onSuccess();
      },
      onError: () => {
        saveHandlers.onError();
      }
    })
  }

  return (
    <Combobox>
      {({ activeOption }) => (
        <>
          <Combobox.Options
            as="div"
            static
            className="flex divide-y divide-gray-100 flex-col sm:flex-row sm:divide-x h-full"
          >
            <div className="h-full min-w-0 h-80 flex-auto scroll-py-4 overflow-y-auto px-6 py-4">
              <div className="-mx-2 text-sm text-gray-700">
                {emailTemplates.map(
                  (template) => (
                    <Combobox.Option
                      as="div"
                      key={template.id}
                      value={template}
                      onClick={() => handleSetSelected(template)}
                      className={({ active }) =>
                        classNames(active || selected.id === template.id ? "bg-gray-100 text-gray-900" : "",
                          "flex cursor-pointer select-none items-center rounded-md p-2"
                        )
                      }
                    >
                      {({ active }) => (
                        <>
                          <span className="ml-3 flex-auto truncate">
                            {template.title}
                          </span>
                          {Boolean(active || selected.id === template.id) && (
                            <ChevronRightIcon
                              className="ml-3 h-5 w-5 flex-none text-gray-400"
                              aria-hidden="true"
                            />
                          )}
                        </>
                      )}
                    </Combobox.Option>
                  )
                )}
              </div>
            </div>

            {selected.id ? (
              <div className="h-full w-full flex-none grow flex-col divide-y divide-gray-100 overflow-y-auto flex sm:w-1/2">
                <div className="p-6">
                  <div className="mb-4">
                    <label>Email:</label>
                    <input value={email} onChange={((e) => setEmail(e.target.value))} type="text" className="text-sm w-full rounded-md border-gray-600" />
                  </div>

                  <div>
                    <label>Content:</label>
                  </div>
                  <textarea style={{ resize: "none" }} value={content} onChange={((e) => setContent(e.target.value))} rows={18} className="text-sm w-full rounded-md border-gray-600" />
                </div>


                <div className="flex flex-auto flex-col justify-between p-6">
                  <button
                    onClick={() => handleSendEmail()}
                    type="button"
                    className="w-full rounded-md border border-transparent bg-primary-600 py-2 px-4 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                  >
                    Send
                  </button>
                </div>
              </div>
            ) : (
              <div className="h-80 w-full sm:h-96 sm:w-1/2"></div>
            )}
          </Combobox.Options>
        </>
      )}
    </Combobox>
  )
}